<template>
  <div
    v-if="estimate"
    :class="[
      'portrait-estimate-document',
      { 'english-document': isEnglish, 'annual-document': isAnnual }
    ]"
    data-estimate-pdf
    data-estimate-pdf-ready="true"
  >
    <section
      v-for="(page, pageIndex) in pages"
      :key="`estimate-page-${pageIndex}`"
      :class="['portrait-estimate-page', { 'page-break': pageIndex > 0 }]"
      data-estimate-pdf-page
    >
      <div v-if="isDraft" class="draft-watermark">DRAFT</div>

      <!-- 見出し・宛先・会社情報・押印欄・金額欄は、提供見本（docs/見積書/縦）の配置に合わせる -->
      <header class="document-header">
        <div class="title-block">
          <h1>{{ label('御見積書', 'QUOTATION') }}</h1>
          <div v-if="pages.length > 1" class="continuation-label">
            {{ label(`（${pageIndex + 1}枚目）`, `(Page ${pageIndex + 1})`) }}
          </div>
        </div>

        <div class="recipient-block">
          <template v-if="isEnglish">
            <div class="recipient-line recipient-line--main">{{ recipientName }}</div>
            <div v-if="estimate.recipient?.postal_code">{{ estimate.recipient.postal_code }}</div>
            <div>{{ estimate.recipient?.address || '' }}</div>
          </template>
          <template v-else>
            <div v-if="recipientSubLine" class="recipient-line">{{ recipientSubLine }}</div>
            <div class="recipient-line recipient-line--main">
              <span>{{ recipientMainLine }}</span>
              <span class="honorific">{{ recipientHonorific }}</span>
            </div>
          </template>
        </div>

        <div class="issuer-area">
          <div class="document-meta">
            <div>No.{{ estimate.estimate_no }}</div>
            <div>{{ formatDate(estimate.issue_date) }}</div>
            <div v-if="Number(estimate.revision_no) > 0">
              {{ formatRevisionLabel(estimate.revision_no) }}
            </div>
          </div>
          <div
            :class="[
              'company-block',
              { 'company-block--with-seal': pageIndex === 0 && Boolean(estimate.seal_flg) }
            ]"
          >
            <div class="company-name">
              {{ label('株式会社 創 英 企 画', 'Soei Planning Co., Ltd.') }}
            </div>
            <div>{{ label('東京都渋谷区代々木2丁目38番6号', '2-38-6 Yoyogi, Shibuya-ku, Tokyo 151-0053, Japan') }}</div>
            <div>{{ label('TEL 03-3370-8006(代表)', 'Tel: 03-3370-8006') }}</div>
            <div>{{ label('FAX 03-3370-8157', 'Fax: 03-3370-8157') }}</div>
            <CompanySeal
              :visible="pageIndex === 0 && Boolean(estimate.seal_flg)"
              class="company-seal"
            />
          </div>
        </div>
      </header>

      <div class="subject-row">
        <div class="subject-lines">
          <div class="subject-line">
            <span>{{ primarySubjectLabel }}{{ label('：', ': ') }}</span>{{ primarySubject }}
          </div>
          <div class="subject-line">
            <span>{{ secondarySubjectLabel }}{{ label('：', ': ') }}</span>{{ secondarySubject }}
          </div>
        </div>
        <div class="approval-stamp" role="group" aria-label="approval stamp">
          <span>{{ label('承認', 'Approved') }}</span>
          <span>{{ label('確認', 'Reviewed') }}</span>
          <span>{{ label('作成', 'Prepared') }}</span>
          <span>
            <EmployeeSeal
              :name="estimate.approved_stamp_name || estimate.approved_by_user?.name || ''"
              :image-url="estimate.approved_stamp_image_url || ''"
              :size="36"
              class="approval-employee-seal"
            />
          </span>
          <span></span>
          <span>
            <EmployeeSeal
              :name="estimate.created_stamp_name || estimate.created_by_user?.name || ''"
              :image-url="estimate.created_stamp_image_url || ''"
              :size="36"
              class="approval-employee-seal"
            />
          </span>
        </div>
      </div>

      <div class="amount-row">
        <div class="grand-total">
          <span class="grand-total-label">
            {{ isAnnual ? label('合 計 金 額', 'Total Amount') : label('見積合計金額', 'Total Quotation Amount') }}
          </span>
          <strong>{{ grandTotalCurrency(displayTotalAmount) }}</strong>
          <small v-if="isAnnual">{{ amountSuffix }}</small>
        </div>
        <div v-if="!isAnnual" class="tax-summary">
          <span>{{ label('見積金額', 'Net Amount') }} {{ currency(netAmount) }}</span>
          <span>{{ label('消費税', 'Tax') }} {{ currency(estimate.tax_amount) }}</span>
        </div>
      </div>

      <table class="estimate-table">
        <thead>
          <tr>
            <th>{{ label('項目', 'Description') }}</th>
            <th class="quantity-column">{{ label('数量', 'Qty.') }}</th>
            <th class="unit-column">{{ label('単位', 'Unit') }}</th>
            <th class="price-column">{{ label('単価', 'Unit Price') }}</th>
            <th class="amount-column">{{ label('金額', 'Amount') }}</th>
            <th class="remarks-column">{{ label('摘要', 'Notes') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in page.rows" :key="row.key">
            <!-- 注意書きは参考資料どおり「項目」列の中で折り返し、他の列の罫線は残す -->
            <tr v-if="row.kind === 'note'" class="note-row">
              <td class="multiline">{{ row.text }}</td><td></td><td></td><td></td><td></td><td></td>
            </tr>
            <tr v-else-if="row.kind === 'line' && row.line.line_type === 'note'" class="note-row">
              <td class="multiline">{{ lineNote(row.line) }}</td><td></td><td></td><td></td><td></td><td></td>
            </tr>
            <tr v-else-if="row.kind === 'line' && row.line.line_type === 'subtotal'" class="subtotal-row">
              <td colspan="4">{{ label('小 計', 'Subtotal') }}</td>
              <td colspan="2" class="number-cell">{{ lineAmount(row.line) }}</td>
            </tr>
            <tr v-else-if="row.kind === 'line'" class="estimate-detail-row">
              <td class="multiline">{{ lineName(row.line) }}</td>
              <td class="number-cell">{{ quantity(row.line) }}</td>
              <td class="center-cell">{{ lineUnit(row.line) }}</td>
              <td class="number-cell">{{ unitPrice(row.line) }}</td>
              <td class="number-cell">{{ lineAmount(row.line) }}</td>
              <td class="multiline">{{ lineRemarks(row.line) }}</td>
            </tr>
            <template v-else-if="row.kind === 'blank'">
              <tr class="blank-row"><td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td></tr>
            </template>
            <tr v-else class="blank-row"><td colspan="6">&nbsp;</td></tr>
          </template>
          <template v-if="pageIndex === pages.length - 1">
            <template v-if="isAnnual">
              <tr class="subtotal-row">
                <td colspan="4">{{ label('小 計', 'Subtotal') }}</td>
                <td colspan="2" class="number-cell">{{ number(netAmount) }}</td>
              </tr>
              <tr class="total-row">
                <td colspan="4">{{ label('合 計', 'Total') }}</td>
                <td colspan="2" class="number-cell">{{ number(netAmount) }}</td>
              </tr>
            </template>
            <template v-else-if="isJapaneseTemporary">
              <tr class="subtotal-row">
                <td colspan="4">小 計</td>
                <td colspan="2" class="number-cell">{{ number(estimate.subtotal_amount) }}</td>
              </tr>
              <tr v-if="hasDiscount" class="subtotal-row">
                <td colspan="4">お値引き</td>
                <td colspan="2" class="number-cell">-{{ number(estimate.discount_amount) }}</td>
              </tr>
              <tr class="subtotal-row">
                <td colspan="4">税抜金額</td>
                <td colspan="2" class="number-cell">{{ number(netAmount) }}</td>
              </tr>
              <tr class="subtotal-row">
                <td colspan="4">{{ formatEstimateTaxLabel(estimate.tax_rate) }}</td>
                <td colspan="2" class="number-cell">{{ number(estimate.tax_amount) }}</td>
              </tr>
              <tr class="total-row">
                <td colspan="4">合 計（税込）</td>
                <td colspan="2" class="number-cell">{{ number(estimate.total_amount) }}</td>
              </tr>
            </template>
            <tr v-else class="total-row">
              <td colspan="4">{{ label('合計', 'Total') }}</td>
              <td colspan="2" class="number-cell">{{ number(netAmount) }}</td>
            </tr>
          </template>
          <tr v-else class="total-row">
            <td colspan="4">{{ label('次頁へ', 'Carried Forward') }}</td>
            <td colspan="2"></td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="pageIndex === pages.length - 1 && generalNotes"
        class="general-notes"
      >
        <strong>{{ label('備考：', 'Notes:') }}</strong>
        <span>{{ generalNotes }}</span>
      </div>

      <div class="footer-notes">
        <div v-if="pageIndex === pages.length - 1" class="quotation-conditions">
          <div v-for="condition in quotationConditions" :key="condition">・{{ condition }}</div>
        </div>
        <div v-if="isDraft" class="draft-note">
          {{ label('※本書は承認前の下書きです。', 'This quotation is a draft pending internal approval.') }}
        </div>
      </div>
      <div v-if="pages.length > 1" class="page-number">{{ pageIndex + 1 }} / {{ pages.length }}</div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import CompanySeal from '@/components/shared/CompanySeal.vue';
import EmployeeSeal from '@/components/shared/EmployeeSeal.vue';
import {
  fillRowsToCapacity,
  formatEstimateQuantity,
  formatEstimateTaxLabel,
  isEnglishEstimateLanguage,
  paginateRowsByWeight,
  rowsDisplayWeight,
  wrappedTextLineCount
} from '@/utils/estimateEditor';
import { formatRevisionLabel } from '@/utils/estimates';
import {
  formatEnglishGregorianDate,
  formatJapaneseGregorianDate
} from '@/utils/estimatePdfDates';

const ROWS_PER_PAGE = 31;

const props = defineProps({
  estimate: {
    type: Object,
    required: true
  },
  variant: {
    type: String,
    required: true,
    validator: value => ['temporary', 'annual'].includes(value)
  }
});

const isAnnual = computed(() => props.variant === 'annual');
const isEnglish = computed(() => isEnglishEstimateLanguage(props.estimate?.default_language_type));
const isJapaneseTemporary = computed(() => !isAnnual.value && !isEnglish.value);
const hasDiscount = computed(() => Number(props.estimate?.discount_amount || 0) > 0);
const isDraft = computed(() => props.estimate?.approval_status !== 'approved');
const label = (japanese, english) => isEnglish.value ? english : japanese;

const netAmount = computed(() =>
  Math.max(
    Number(props.estimate?.subtotal_amount || 0) -
      Number(props.estimate?.discount_amount || 0),
    0
  )
);
// 年間見積は見本どおり税抜の年額を表示する（2026/09/28 お客様確認）
const displayTotalAmount = computed(() =>
  isAnnual.value ? netAmount.value : Number(props.estimate?.total_amount || 0)
);
const amountSuffix = computed(() => {
  if (isAnnual.value) return label('（年額）', '(Annual)');
  return label('（税込）', '(Tax included)');
});
const recipientName = computed(() => {
  const recipient = props.estimate?.recipient || {};
  if (isEnglish.value) {
    return [recipient.customer_nm, recipient.department_nm].filter(Boolean).join(', ');
  }
  return [recipient.customer_nm, recipient.department_nm, '御中']
    .filter(Boolean)
    .join(' ');
});
// 見本どおり、部署がある場合は「顧客名」を上段に小さく、「部署名 御中」を下線付きの下段に表示する
const recipientMainLine = computed(() => {
  const recipient = props.estimate?.recipient || {};
  return recipient.department_nm || recipient.customer_nm || '';
});
const recipientSubLine = computed(() => {
  const recipient = props.estimate?.recipient || {};
  return recipient.department_nm ? recipient.customer_nm || '' : '';
});
const recipientHonorific = computed(() => props.estimate?.recipient?.honorific || '御中');
const estimateTitle = computed(() => props.estimate?.title || '');
const buildingName = computed(() =>
  props.estimate?.building_snapshot?.building_nm || props.estimate?.building_nm || estimateTitle.value
);
const workLocation = computed(() =>
  props.estimate?.work_location ||
  [
    props.estimate?.building_snapshot?.building_nm,
    props.estimate?.building_snapshot?.address
  ].filter(Boolean).join(isEnglish.value ? ', ' : '　') ||
  '-'
);
const primarySubjectLabel = computed(() =>
  isAnnual.value ? label('件名', 'Subject') : label('物件名', 'Property')
);
const secondarySubjectLabel = computed(() =>
  isAnnual.value ? label('履行場所', 'Service Location') : label('見積件名', 'Quotation Subject')
);
const primarySubject = computed(() => isAnnual.value ? estimateTitle.value : buildingName.value);
const secondarySubject = computed(() => isAnnual.value ? workLocation.value : estimateTitle.value);
const generalNotes = computed(() => props.estimate?.general_notes || '');
const quotationConditions = computed(() => {
  const conditions = [];
  if (props.estimate?.valid_until) {
    const date = formatDate(props.estimate.valid_until);
    conditions.push(label(`本見積の有効期限は${date}までです。`, `This quotation is valid until ${date}.`));
  }
  return conditions;
});
const annualTaxNote = computed(() =>
  label('※本見積に消費税は含んでおりません', '* Consumption tax is not included in this quotation.')
);

const allRows = computed(() => {
  const rows = [];
  (props.estimate?.sections || []).forEach((section, sectionIndex) => {
    (section.lines || []).forEach((line, lineIndex) => {
      rows.push({
        key: `section-${section.id || sectionIndex}-line-${line.id || lineIndex}`,
        kind: 'line',
        line
      });
    });
  });
  (props.estimate?.unsectioned_lines || []).forEach((line, lineIndex) => {
    rows.push({
      key: `unsectioned-line-${line.id || lineIndex}`,
      kind: 'line',
      line
    });
  });
  // 年間見積は見本どおり税抜で出すため、消費税を含まない旨を項目列に必ず記載する
  if (isAnnual.value) {
    rows.push({
      key: 'annual-tax-note',
      kind: 'note',
      text: annualTaxNote.value,
      anchorBottom: true
    });
  }
  return rows;
});

const portraitPdfRowWeight = row => {
  if (row.kind === 'blank') return 1;
  if (row.kind === 'note') return wrappedTextLineCount(row.text, 30);
  if (row.kind === 'line' && row.line?.line_type === 'note') {
    return wrappedTextLineCount(lineNote(row.line), 30);
  }
  if (row.kind === 'line') {
    return Math.max(
      wrappedTextLineCount(lineName(row.line), 30),
      wrappedTextLineCount(lineRemarks(row.line), 33)
    );
  }
  return 1;
};

const pages = computed(() => {
  const sourceRows = allRows.value;
  const bottomRows = sourceRows.filter(row => row.anchorBottom);
  const regularRows = sourceRows.filter(row => !row.anchorBottom);
  const rowsPerPage = ROWS_PER_PAGE;
  const conditionRows = quotationConditions.value.length > 0 ? 2 : 0;
  const generalNotesRows = generalNotes.value
    ? wrappedTextLineCount(generalNotes.value, 82) + 1
    : 0;
  const totalRows = isAnnual.value
    ? 2
    : isJapaneseTemporary.value
      ? (hasDiscount.value ? 5 : 4)
      : 1;
  const bottomRowsWeight = rowsDisplayWeight(bottomRows, portraitPdfRowWeight);
  const summaryRows = totalRows + conditionRows + generalNotesRows;
  const result = paginateRowsByWeight(
    regularRows,
    rowsPerPage,
    bottomRowsWeight + summaryRows,
    portraitPdfRowWeight
  );

  return result.map((sourcePage, pageIndex) => {
    const isFinalPage = pageIndex === result.length - 1;
    const rowsWithBottomNotes = [
      ...sourcePage,
      ...(isFinalPage ? bottomRows : [])
    ];
    return {
      rows: fillRowsToCapacity(
        rowsWithBottomNotes,
        rowsPerPage,
        isFinalPage ? summaryRows : 1,
        portraitPdfRowWeight,
        `page-${pageIndex}-blank`
      )
    };
  });
});

const formatDate = value => {
  return isEnglish.value
    ? formatEnglishGregorianDate(value)
    : formatJapaneseGregorianDate(value);
};

const number = value =>
  Math.round(Number(value || 0)).toLocaleString(isEnglish.value ? 'en-US' : 'ja-JP');
const currency = value => `${isEnglish.value ? '¥' : '￥'}${number(value)}`;
const grandTotalCurrency = value => isEnglish.value ? currency(value) : `${currency(value)}.-`;
const lineName = line => {
  const defaultName = line.line_type === 'discount'
    ? label('値引', 'Discount')
    : line.line_type === 'note'
      ? label('注記', 'Note')
      : '';
  return line.item_nm || defaultName;
};
// 値引は金額だけを表示する（数量・単位・単価は出さない）
const lineUnit = line => line.line_type === 'discount' ? '' : line.unit_nm;
const lineRemarks = line => [
  line.part_nm,
  line.specification,
  line.notes
].filter(Boolean).join('\n');
const lineNote = line => [
  line.item_nm,
  line.specification,
  line.notes
].filter(Boolean).join('\n') || label('※注記', '* Note');
const quantity = line => {
  if (['note', 'subtotal', 'discount'].includes(line.line_type) || line.quantity == null) return '';
  return formatEstimateQuantity(
    line.quantity,
    isEnglish.value ? 'en-US' : 'ja-JP',
    line.quantity_decimal_places
  );
};
const unitPrice = line => {
  if (['note', 'subtotal', 'discount'].includes(line.line_type) || line.unit_price == null) return '';
  return number(line.unit_price);
};
const lineAmount = line => {
  if (line.line_type === 'note') return '';
  const value = number(line.amount);
  return line.line_type === 'discount' ? `-${value}` : value;
};
</script>

<style scoped>
.portrait-estimate-document {
  --pdf-rule: 1px solid #666;
  --pdf-rule-dark: 1px solid #555;
  width: 210mm;
  color: #111;
  background: #fff;
  font-family: "Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", "MS Mincho", serif;
  font-size: 8pt;
  line-height: 1.22;
}

.portrait-estimate-document.english-document {
  font-family: "Times New Roman", Times, serif;
}

.portrait-estimate-page {
  position: relative;
  box-sizing: border-box;
  width: 210mm;
  height: 297mm;
  padding: 34mm 8mm 6mm;
  overflow: hidden;
  background: #fff;
}

.annual-document .portrait-estimate-page {
  padding-top: 8mm;
}

.page-break {
  break-before: page;
  page-break-before: always;
}

.draft-watermark {
  position: absolute;
  top: 118mm;
  left: 24mm;
  z-index: 0;
  color: rgba(180, 0, 0, 0.07);
  font-family: Arial, sans-serif;
  font-size: 64pt;
  font-weight: 700;
  letter-spacing: 10mm;
  transform: rotate(-18deg);
}

.portrait-estimate-page > *:not(.draft-watermark) {
  position: relative;
  z-index: 1;
}

.document-header {
  display: grid;
  min-height: 47mm;
  grid-template-areas:
    "title title"
    "recipient issuer";
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto 1fr;
  gap: 4mm 6mm;
}

.title-block {
  grid-area: title;
}

.title-block h1 {
  margin: 0;
  font-size: 20pt;
  font-weight: 400;
  letter-spacing: 2mm;
  text-align: center;
}

.english-document .title-block h1 {
  letter-spacing: 1mm;
}

.continuation-label {
  margin-top: 1mm;
  text-align: center;
}

.recipient-block {
  grid-area: recipient;
  align-self: start;
  padding-top: 9mm;
  font-size: 9pt;
}

.recipient-line {
  min-height: 4.5mm;
}

.recipient-line--main {
  display: flex;
  width: 82mm;
  padding-bottom: 0.5mm;
  border-bottom: var(--pdf-rule);
  align-items: baseline;
  justify-content: space-between;
  gap: 4mm;
  font-size: 10pt;
}

.honorific {
  padding-right: 2mm;
  white-space: nowrap;
}

.issuer-area {
  grid-area: issuer;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 8.5pt;
}

.document-meta {
  min-height: 9mm;
  margin-bottom: 1.5mm;
  line-height: 1.5;
  text-align: right;
}

.company-block {
  position: relative;
  box-sizing: border-box;
  line-height: 1.35;
  text-align: left;
}

.company-block--with-seal {
  padding-top: 17.5mm;
}

.company-seal {
  position: absolute;
  top: 0;
  right: 16mm;
}

.company-name {
  margin-bottom: 0.5mm;
  font-size: 14pt;
  letter-spacing: 0.6mm;
}

.subject-row {
  display: flex;
  margin: 2mm 0 1.5mm;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8mm;
}

.subject-lines {
  width: 100mm;
}

.subject-line {
  min-height: 5.5mm;
  padding: 0.8mm 0.5mm 0.3mm;
  border-bottom: var(--pdf-rule);
  font-size: 9pt;
}

.approval-stamp {
  display: grid;
  width: 52mm;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 4.5mm 14mm;
  border-top: var(--pdf-rule);
  border-left: var(--pdf-rule);
  font-size: 7pt;
}

.approval-stamp > span {
  display: flex;
  box-sizing: border-box;
  padding: 0.3mm;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
  align-items: center;
  justify-content: center;
  font-weight: 400;
  text-align: center;
}

.approval-stamp > span:nth-child(n + 4) {
  position: relative;
  padding: 0.5mm;
  font-size: 6pt;
}

.approval-stamp .approval-employee-seal {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 36px;
  height: 36px;
  margin: auto;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

/* 金額欄は見本どおり明細表と一体の枠にする（下罫線は明細表の上罫線を使う） */
.amount-row {
  display: flex;
  min-height: 10mm;
  padding: 1.5mm 3mm;
  align-items: baseline;
  gap: 6mm;
  border: var(--pdf-rule);
  border-bottom: 0;
}

.grand-total {
  display: flex;
  align-items: baseline;
  gap: 6mm;
}

.grand-total-label {
  font-size: 13pt;
  font-weight: 700;
}

.grand-total strong {
  font-size: 16pt;
  font-weight: 400;
  white-space: nowrap;
}

.grand-total small {
  font-size: 11pt;
  white-space: nowrap;
}

.tax-summary {
  display: flex;
  margin-left: auto;
  gap: 8mm;
  font-size: 10pt;
  white-space: nowrap;
}

.estimate-table {
  border-top: var(--pdf-rule);
  border-left: var(--pdf-rule);
  table-layout: fixed;
}

.estimate-table th,
.estimate-table td {
  height: 4.55mm;
  box-sizing: border-box;
  padding: 0.45mm 1mm;
  border: 0;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
  vertical-align: middle;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* 見本どおり日本語の見出しは字間をあける */
.portrait-estimate-document:not(.english-document) .estimate-table thead th {
  letter-spacing: 0.8em;
  text-indent: 0.8em;
}

.portrait-estimate-document:not(.english-document) .estimate-table thead th:first-child {
  letter-spacing: 3em;
  text-indent: 3em;
}

.portrait-estimate-document:not(.english-document) .estimate-table thead th:nth-child(2),
.portrait-estimate-document:not(.english-document) .estimate-table thead th:nth-child(3) {
  letter-spacing: 0;
  text-indent: 0;
}

.portrait-estimate-document:not(.english-document) .estimate-table thead th:nth-child(4),
.portrait-estimate-document:not(.english-document) .estimate-table thead th:nth-child(5) {
  letter-spacing: 0.4em;
  text-indent: 0.4em;
}

.estimate-table thead th {
  height: 6mm;
  background: #fff;
  font-size: 8.5pt;
  font-weight: 400;
  text-align: center;
}

.quantity-column {
  width: 10mm;
}

.unit-column {
  width: 10mm;
}

.price-column {
  width: 15mm;
}

.amount-column {
  width: 18mm;
}

.remarks-column {
  width: 64mm;
}

.section-row td {
  font-weight: 400;
}

.section-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4mm;
}

.section-content > :last-child {
  color: #444;
  font-size: 7.5pt;
  font-weight: 400;
}

.note-row td:first-child {
  padding-left: 2mm;
}

.subtotal-row,
.total-row {
  font-weight: 400;
  text-align: right;
}

.number-cell {
  white-space: nowrap;
  text-align: right;
}

.center-cell {
  text-align: center;
}

.multiline {
  white-space: pre-wrap;
}

.footer-notes {
  display: flex;
  min-height: 5mm;
  padding-top: 1mm;
  justify-content: space-between;
  gap: 4mm;
  font-size: 7pt;
}

.general-notes {
  display: flex;
  min-height: 6mm;
  margin-top: 1mm;
  padding: 1mm 1.5mm;
  border: var(--pdf-rule-dark);
  gap: 1.5mm;
  overflow: hidden;
  font-size: 7pt;
  white-space: pre-line;
}

.general-notes strong {
  flex: 0 0 auto;
  font-weight: 400;
}

.quotation-conditions {
  flex: 1;
  white-space: pre-line;
}

.draft-note {
  color: #a00;
  text-align: right;
}

.page-number {
  position: absolute !important;
  right: 8mm;
  bottom: 2mm;
  left: 8mm;
  text-align: center;
}
</style>
